import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog } from '@mui/material'
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import SearchIcon from '@mui/icons-material/Search'
import { useSelector } from 'react-redux'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import {
  fetchSecurityCheckFamilyByCategory,
  getSecurityControls,
} from '../../redux/securityCheckFamily/securityCheckFamilyAction'
import { resetSecurityCheckFamily } from '../../redux/securityCheckFamily/securityCheckFamilySlice'
import { useSearchParams } from 'react-router-dom'
import {
  getSubDomainbySecurityControls,
  updateSubDomain,
} from '../../redux/subDomains/subDomainActions'
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid'
import { SubDomain } from '../../types/subDomainTypes'
import { addMaturityScoring } from '../../redux/maturityScoring/maturityScoringActions'
import { Maturity } from '../../types/maturityScoringTypes'
import { SecurityControls } from '../../types/securityControls'
import { updateSecuritycontrols } from '../../redux/securityControls/securityControlsAction'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import UseAlert from '../../components/common/alert'
import FixWeightTargetScore from '../../components/containers/FixWeightTargetScoreForm'
import { UserRole } from '../../types/keycloak'
import { hasRole } from '../../utils/checkRole'
import ViewAnalysisButton from '../../components/common/ViewAnalysisButton'
import {
  selectSpecificationLoading,
  selectSpecifications,
} from '../../redux/specification/specificationSelectors'
import { selectLoading } from '../../redux/securityCheckFamily/securityCheckFamilySelector'
import { selectUsername } from '../../redux/keycloak/keycloakSelectors'
import { Loader } from 'rsuite'
import MaturityScoringModal from '../../components/containers/MaturityScoringModal'
import { formatTitle } from '../../utils/formatTitle'
import {
  listPageDataGridSx,
  listPageGridSlots,
  listPageIconBtnStyle,
} from '../../components/common/ListPageShell'
import { DetailPageHead } from '../../components/common/detail'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
import '../../components/common/ListPageTable.css'
import './MaturiteList.css'
import { formatDecimal } from '../../utils/formatNumbers'

export default function MaturiteList() {
  const { t, i18n } = useTranslation()
  const language = i18n.language.toLocaleUpperCase()
  const [searchParams, setSearchParams] = useSearchParams()
  const [value, setValue] = useState(() => {
    const tab = searchParams.get('tab')
    return tab ? parseInt(tab, 10) : 0
  })
  const [search, setSearch] = useState('')

  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>(() => {
    const panel = searchParams.get('panel')
    return panel ? { [panel]: true } : {}
  })
  const location = useLocation()
  const focusTarget = useState(() => {
    const incoming = location.state as {
      highlightSecurityCheckFamily?: string
      highlightSecurityControl?: string
      highlightSubDomain?: string
      familyLabel?: string
      controlLabel?: string
      subDomainLabel?: string
    } | null
    if (
      !incoming?.highlightSecurityCheckFamily ||
      !incoming?.highlightSecurityControl
    ) {
      return null
    }
    return {
      familyId: incoming.highlightSecurityCheckFamily,
      controlId: incoming.highlightSecurityControl,
      subDomainId: incoming.highlightSubDomain || '',
      familyLabel: incoming.familyLabel || '',
      controlLabel: incoming.controlLabel || '',
      subDomainLabel: incoming.subDomainLabel || '',
    }
  })[0]
  const focusApplied = useRef(false)
  const focusFamilyApplied = useRef(false)
  const focusScrolled = useRef(false)

  useEffect(() => {
    if (document.querySelector('.MuiDialog-root')) return
    const body = document.body
    if (body.style.overflow !== 'hidden') return
    body.style.overflow = ''
    body.style.height = ''
    body.style.position = ''
    body.style.paddingRight = ''
    body.style.boxSizing = ''
  }, [])

  const handleChangeExp =
    (
      panel: string,
      controlId: string,
      controlLabel?: string,
      familyLabel?: string
    ) =>
    (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded((prevExpanded) => {
        const newState = Object.keys(prevExpanded).reduce(
          (acc, key) => {
            acc[key] = false
            return acc
          },
          {} as { [key: string]: boolean }
        )
        if (newExpanded) {
          newState[panel] = true
        }

        return newState
      })

      if (newExpanded) {
        setSecurityControl(controlId)
        // Sync expanded state into URL so browser back/refresh restores it
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            next.set('panel', panel)
            if (controlLabel) next.set('controlLabel', controlLabel)
            if (familyLabel) next.set('familyLabel', familyLabel)
            return next
          },
          { replace: true }
        )
      } else {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            next.delete('panel')
            next.delete('controlLabel')
            next.delete('familyLabel')
            return next
          },
          { replace: true }
        )
      }
    }
  const dispatch: AppDispatch = useAppDispatch()

  const securityAll = useSelector(
    (state: RootState) =>
      state.securitycheckFamily.SecurityCheckFamilyResponse
        ?.securityCheckFamilies
  )
  const loadingSecurityCheckFamily = useSelector(selectLoading)

  const securityControls = useSelector(
    (state: RootState) => state.securitycheckFamily.SecurityControls
  )

  const subDomains = useSelector(
    (state: RootState) => state.subDomain.subDomainsbySecurityControls
  )

  const loading = useSelector((state: RootState) => state.subDomain.loading)
  const [SecurityCheckFamily, setSecurityCheckFamily] = useState<string>('')
  const [fileName, setFileName] = useState('')
  const [SecurityControl, setSecurityControl] = useState<string>('')
  const showLoader = useTableLoadingGate(loading, [SecurityControl])

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })
  const specsLoading = useSelector(selectSpecificationLoading)
  const selectedNorm = useSelector(selectSpecifications)[0]?.NormBase || ''
  const familiesReady = Boolean(securityAll?.length)
  // Only block the whole card until families exist. Control refetches share the
  // same Redux `loading` flag and must not remount / wipe the family chips.
  const showFamiliesLoader =
    specsLoading || !selectedNorm || (loadingSecurityCheckFamily && !familiesReady)

  // Drop stale families/controls left by other pages (e.g. mapping) so we
  // never paint the wrong scope then "auto-refresh" into the programme norm.
  useEffect(() => {
    dispatch(resetSecurityCheckFamily())
  }, [dispatch])

  useEffect(() => {
    if (specsLoading || !selectedNorm) return

    const action = fetchSecurityCheckFamilyByCategory({
      category: selectedNorm,
      page: 1,
      pageSize: 200,
      language,
    })
    if (action) dispatch(action)
  }, [dispatch, selectedNorm, specsLoading, language])

  useEffect(() => {
    if (!securityAll?.length) return
    const activeFamilyId = securityAll[value]?._id || ''
    if (!activeFamilyId) return
    setSecurityCheckFamily(activeFamilyId)
    dispatch(getSecurityControls(activeFamilyId))
  }, [dispatch, securityAll, value])

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
    const selectedSecurity = securityAll?.[newValue]

    if (selectedSecurity) {
      setSecurityCheckFamily(selectedSecurity._id || '')
    }
    // Update tab index in URL, clear panel state since tab changed
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('tab', String(newValue))
        next.delete('panel')
        next.delete('controlLabel')
        next.delete('familyLabel')
        return next
      },
      { replace: true }
    )
    setExpanded({})
  }
  const [SubDomain, setSubDomain] = useState({
    _id: '',
    SubDomainId: '',
    Description: '',
    Weight: 0,
    TargetScore: 0,
    Label: '',
    SecurityControlId: '',
    LabelSCF: '',
  })

  useEffect(() => {
    if (SecurityControl) {
      dispatch(getSubDomainbySecurityControls(SecurityControl))
    }
  }, [SecurityControl])

  useEffect(() => {
    if (!focusTarget || focusFamilyApplied.current || !securityAll?.length) return
    const idx = securityAll.findIndex((family) => family._id === focusTarget.familyId)
    if (idx < 0) return
    focusFamilyApplied.current = true
    if (value !== idx) setValue(idx)
  }, [focusTarget, securityAll, value])

  useEffect(() => {
    if (!focusTarget || focusApplied.current || !securityControls?.length) return
    if (securityAll?.[value]?._id !== focusTarget.familyId) return
    const ctrlIndex = securityControls.findIndex(
      (control) => control._id === focusTarget.controlId
    )
    if (ctrlIndex < 0) return
    const matched = securityControls[ctrlIndex]
    if (
      matched.SecurityCheckFamilyId &&
      matched.SecurityCheckFamilyId !== focusTarget.familyId
    ) {
      return
    }
    const panelId = `panel${value}-${ctrlIndex}`
    focusApplied.current = true
    setSearch('')
    setExpanded({ [panelId]: true })
    setSecurityControl(focusTarget.controlId)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('tab', String(value))
        next.set('panel', panelId)
        if (matched.Label) next.set('controlLabel', matched.Label)
        const familyLabel = securityAll?.[value]?.LabelSCF
        if (familyLabel) next.set('familyLabel', familyLabel)
        return next
      },
      { replace: true }
    )
  }, [focusTarget, securityAll, securityControls, setSearchParams, value])

  useEffect(() => {
    if (!focusTarget || !focusApplied.current || focusScrolled.current) return
    const panel = searchParams.get('panel')
    if (!panel || !expanded[panel]) return
    const row = document.querySelector(`[data-maturity-panel="${panel}"]`)
    if (!row) return
    focusScrolled.current = true
    row.scrollIntoView({ block: 'start', behavior: 'auto' })
  }, [expanded, focusTarget, searchParams])

  // When returning via browser back button: securityControls loads from the tab restore,
  // and we need to also rehydrate SecurityControl from the panel URL param.
  useEffect(() => {
    if (!securityControls || securityControls.length === 0) return
    const panel = searchParams.get('panel')
    if (!panel) return
    // panel format is `panel{tabIndex}-{ctrlIndex}`, e.g. "panel0-2"
    const match = panel.match(/^panel\d+-(\d+)$/)
    if (!match) return
    const ctrlIndex = parseInt(match[1], 10)
    const control = securityControls[ctrlIndex]
    if (control?._id) {
      setSecurityControl(control._id)
    }
  }, [securityControls])

  const [open, setOpen] = React.useState(false)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const [open2, setOpen2] = React.useState(false)
  const handleOpen2 = () => setOpen2(true)
  const handleClose2 = () => {
    setFileName('')
    setOpen2(false)
  }
  const handleLaunchClick = (suBDomain: SubDomain) => {
    handleOpen()
    if (suBDomain._id) {
      setSubDomain({
        _id: suBDomain._id,
        SubDomainId: suBDomain.SubDomainId,
        Description: suBDomain.Description,
        Weight: suBDomain.Weight || -1,
        TargetScore: suBDomain.TargetScore || -1,
        Label: suBDomain.Label,
        SecurityControlId: suBDomain.SecurityControlId,
        LabelSCF: suBDomain.LabelSCF,
      })
    }
  }

  const handleInputChangeSub = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    const numericValue = Number(value)

    if (name === 'Weight' && numericValue > 4) return
    if (name === 'TargetScore' && numericValue > 5) return

    setSubDomain((prevSubDomain) => ({
      ...prevSubDomain,
      [name]: numericValue,
    }))
  }
  const navigate = useNavigate()
  const handleUpdateSubDomain = async () => {
    try {
      if (
        SubDomain.Description &&
        SubDomain.TargetScore &&
        SubDomain._id &&
        SubDomain.Label &&
        SubDomain.Weight
      ) {
        await dispatch(
          updateSubDomain({
            updatedSubDomain: {
              _id: SubDomain._id,
              SubDomainId: SubDomain.SubDomainId,
              Description: SubDomain.Description,
              Weight: SubDomain.Weight,
              TargetScore: SubDomain.TargetScore,
              Label: SubDomain.Label,
              SecurityControlId: SubDomain.SecurityControlId,
              LabelSCF: SubDomain.LabelSCF,
            },
            _id: SubDomain._id,
          })
        )

        dispatch(getSubDomainbySecurityControls(SecurityControl))
        dispatch(getSecurityControls(SecurityCheckFamily))

        console.log(SubDomain)
        setAlert({
          open: true,
          message: t('alerts.subDomainUpdate.success'),
          severity: 'success',
        })
        handleClose()
      } else {
        setAlert({
          open: true,
          message: t('alerts.subDomainUpdate.emptyFields'),
          severity: 'error',
        })
      }
    } catch (error: any) {
      console.error('Error updating SUB DOMAIN:', error.message)
      setAlert({
        open: true,
        message: t('alerts.subDomainUpdate.error'),
        severity: 'error',
      })
    }
  }

  const [Maturity, setMaturity] = useState<Maturity>({
    AnswerScore: 1,
    SubDomainScore: 0,
    SubDomainId: '',
    proof: null,
  })
  const [targetScore, setTargetScore] = useState<number | undefined>()

  const handleLaunchClick1 = (suBDomain: SubDomain) => {
    if (suBDomain._id) {
      setMaturity({
        AnswerScore: 1,
        SubDomainScore: 0,
        SubDomainId: suBDomain._id,
        proof: null,
      })
      setTargetScore(suBDomain.TargetScore)
      setFileName('')
      handleOpen2()
    }
  }
  const handleAnswerScoreChange = (value: number) =>
    setMaturity((prev) => ({ ...prev, AnswerScore: value }))

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setFileName(file.name)
      setMaturity((prev) => ({
        ...prev,
        proof: file,
      }))
    }
  }

  const handleAddMarurity = async (finalScore?: number) => {
    try {
      const answerScore =
        typeof finalScore === 'number' ? finalScore : Maturity.AnswerScore
      if (answerScore && Maturity.proof && Maturity.SubDomainId) {
        const formData = new FormData()
        formData.append('AnswerScore', answerScore.toString())
        formData.append('SubDomainScore', Maturity.SubDomainScore.toString())
        formData.append('SubDomainId', Maturity.SubDomainId)
        formData.append('proof', Maturity.proof)

        await dispatch(addMaturityScoring(formData)).unwrap()

        setAlert({
          open: true,
          message: t('alerts.maturityScoring.addSuccess'),
          severity: 'success',
        })

        dispatch(getSubDomainbySecurityControls(SecurityControl))
        dispatch(getSecurityControls(SecurityCheckFamily))
        handleClose2()
        setMaturity({
          AnswerScore: 1,
          SubDomainScore: 0,
          SubDomainId: '',
          proof: null,
        })
        setFileName('')
      } else {
        setAlert({
          open: true,
          message: t('alerts.maturityScoring.emptyFields'),
          severity: 'error',
        })
      }
    } catch (error: any) {
      console.error('Error adding maturity:', error)

      setAlert({
        open: true,
        message:
          (typeof error === 'string' && error) ||
          error?.message ||
          t('alerts.maturityScoring.addError'),
        severity: 'error',
      })
    }
  }

  // Helper function to generate DataGrid columns with access to the parent control
  const getColumnsWithControlContext = (
    control: any,
    tabIndex: number,
    panelId: string,
    familyLabel: string
  ): GridColDef[] => [
    {
      field: 'Label',
      headerName: t('maturityManagementPage.tableMaturity.Label'),
      flex: 1.4,
      minWidth: 180,
      renderCell: (params) => (
        <button
          type="button"
          className="incyia-name-link maturity-link-btn"
          onClick={() =>
            navigate(`/subDomain/${params.id}`, {
              state: {
                from: {
                  tab: tabIndex,
                  panel: panelId,
                  controlLabel: control?.Label || '',
                  familyLabel: familyLabel,
                  rowLabel: params.row.LabelSD || '',
                },
              },
            })
          }
        >
          <ListPageDescription
            value={formatTitle(params.row.LabelSD || params.row.Label)}
            tone="primary"
          />
        </button>
      ),
    },
    {
      field: 'Source',
      headerName: t('maturityManagementPage.tableMaturity.Source'),
      minWidth: 160,
      flex: 0.9,
      renderCell: (params) => {
        const rowIndex =
          params.api.getAllRowIds().indexOf(params.id.toString()) + 1
        return (
          <span className="maturity-source-cell">
            {`${params.row.Source || '—'}.${rowIndex}`}
          </span>
        )
      },
    },
    {
      field: 'Description',
      headerName: t('maturityManagementPage.tableMaturity.Description'),
      flex: 2,
      minWidth: 240,
      renderCell: (params) => (
        <ListPageDescription value={params.row.Description} />
      ),
    },
    {
      field: 'Weight',
      headerName: t('maturityManagementPage.tableMaturity.Weight'),
      minWidth: 100,
      width: 100,
      flex: 0,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const w = Number(params.row.Weight)
        return (
          <span className={`maturity-num${w < 0 ? ' is-missing' : ''}`}>
            {w < 0 ? '—' : formatDecimal(w)}
          </span>
        )
      },
    },
    {
      field: 'TargetScore',
      headerName: t('maturityManagementPage.tableMaturity.TargetScore'),
      minWidth: 140,
      width: 140,
      flex: 0,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const score = Number(params.row.TargetScore)
        return (
          <span className={`maturity-num${score < 0 ? ' is-missing' : ''}`}>
            {score < 0 ? '—' : formatDecimal(score)}
          </span>
        )
      },
    },
    {
      field: 'Action',
      headerName: t('maturityManagementPage.tableMaturity.Action'),
      minWidth: 96,
      width: 96,
      flex: 0,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridCellParams) => {
        const needsSetup =
          params.row.TargetScore === -1 && params.row.Weight === -1
        const canEdit =
          hasRole(roles, UserRole.RSSI) ||
          hasRole(roles, UserRole.SSI_TEAM) ||
          hasRole(roles, UserRole.ADMIN)
        if (!needsSetup && !canEdit) {
          return <span className="incyia-muted">—</span>
        }
        return (
          <div className="maturity-icon-cell list-row-actions">
            <button
              type="button"
              title={
                needsSetup
                  ? t('popups.fixWeightTargetScore.needsSetup', {
                      defaultValue: 'Needs setup',
                    })
                  : t('listPages.edit', { defaultValue: 'Edit' })
              }
              style={{
                ...listPageIconBtnStyle,
                color: needsSetup ? '#96271B' : undefined,
                background: needsSetup ? '#FAE6E3' : undefined,
              }}
              onClick={() => handleLaunchClick(params.row)}
            >
              {needsSetup ? (
                <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
              ) : (
                <DriveFileRenameOutlineOutlinedIcon sx={{ fontSize: 15 }} />
              )}
            </button>
          </div>
        )
      },
    },
    {
      field: 'Maturity Scoring',
      headerName: t('maturityManagementPage.tableMaturity.MaturityScoring'),
      minWidth: 130,
      width: 130,
      flex: 0,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridCellParams) => {
        const row = params.row
        const canScore =
          row.TargetScore !== -1 &&
          row.Weight !== -1 &&
          control?.IsComplete === true
        if (!canScore) {
          return <span className="incyia-muted">—</span>
        }
        return (
          <div className="maturity-icon-cell list-row-actions">
            {row.hasMaturityScoring ? (
              <NavLink
                to={`/subDomain-Maturityscoring/${row._id}`}
                state={{ targetScore: row.TargetScore }}
                title={t('buttons.view', { defaultValue: 'View' })}
                style={listPageIconBtnStyle}
              >
                <RemoveRedEyeOutlinedIcon sx={{ fontSize: 16 }} />
              </NavLink>
            ) : (
              <button
                type="button"
                title={t('popups.addMaturityScoring.title')}
                style={listPageIconBtnStyle}
                onClick={() => handleLaunchClick1(row)}
              >
                <AddCircleOutlineOutlinedIcon sx={{ fontSize: 16 }} />
              </button>
            )}
          </div>
        )
      },
    },
  ]
  const handleUpdateApplSecurity = async (control: SecurityControls) => {
    const updatedApplicable = !control.Applicable

    await dispatch(
      updateSecuritycontrols({
        updatedSecurityControl: {
          Applicable: updatedApplicable,
          SecurityControlId: control.SecurityControlId,
          Description: control.Description,
          Label: control.Label,
          Source: control.Source,
          LabelSCF: control.LabelSCF,
          UpdatedBy: username || undefined,
          ScopeUpdatedAt: new Date().toISOString(),
        },
        _id: control._id || '',
      })
    )
    dispatch(getSecurityControls(SecurityCheckFamily))

    setAlert({
      open: true,
      message: t('alerts.applicableUpdate.success'),
      severity: 'success',
    })
  }
  const handleBatchApplicable = async (applicable: boolean) => {
    const stamp = new Date().toISOString()
    await Promise.all(
      filteredControls.map(({ control }) =>
        dispatch(
          updateSecuritycontrols({
            updatedSecurityControl: {
              Applicable: applicable,
              SecurityControlId: control.SecurityControlId,
              Description: control.Description,
              Label: control.Label,
              Source: control.Source,
              LabelSCF: control.LabelSCF,
              UpdatedBy: username || undefined,
              ScopeUpdatedAt: stamp,
            },
            _id: control._id || '',
          })
        )
      )
    )

    dispatch(
      getSecurityControls(
        SecurityCheckFamily || securityAll?.[value]?._id || ''
      )
    )
    setAlert({
      open: true,
      message: t('alerts.applicableUpdate.success'),
      severity: 'success',
    })
  }
  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      open: false,
    })
  }
  const roles = useSelector((state: any) => state.auth.roles)
  const username = useSelector(selectUsername)
  const filteredControls = useMemo(() => {
    const query = search.trim().toLowerCase()
    return (securityControls || [])
      .map((control, ctrlIndex) => ({ control, ctrlIndex }))
      .filter(({ control }) => {
        if (!query) return true
        return [control.Label, control.Description, control.Source]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(query))
      })
  }, [search, securityControls])
  const activeFamily = securityAll?.[value]
  const applicableCount = (securityControls || []).filter(
    (control) => control.Applicable
  ).length
  const canManageApplicability =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)

  return (
    <div className="maturity-page">
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      <DetailPageHead
        eyebrow={t('maturityManagementPage.eyebrow', {
          defaultValue: 'Governance',
        })}
        title={formatTitle(t('maturityManagementPage.title'))}
        titleTourId="maturity-page-header"
        subtitle={t('maturityManagementPage.subtitle', {
          defaultValue:
            'Choose which security checks are in scope for this programme, family by family. The analysis view scores only what is in scope.',
        })}
        actions={
          hasRole(roles, UserRole.RSSI) ||
          hasRole(roles, UserRole.DSSI) ||
          hasRole(roles, UserRole.ADMIN) ? (
            <ViewAnalysisButton
              entity="maturity"
              id={''}
              data-tour-id="maturity-view-analysis"
            />
          ) : undefined
        }
      />

      {focusTarget ? (
        <div className="incyia-combo-banner">
          <h3>{t('maturityManagementPage.fixReminder.title')}</h3>
          <p>{t('maturityManagementPage.fixReminder.body')}</p>
          <div className="incyia-combo">
            <div className="incyia-combo-item">
              <span className="incyia-combo-k">
                {t('maturityManagementPage.familyLabel')}
              </span>
              <span className="incyia-combo-v">
                {focusTarget.familyLabel || '—'}
              </span>
            </div>
            <div className="incyia-combo-item">
              <span className="incyia-combo-k">
                {t('Security Control', { defaultValue: 'Security Control' })}
              </span>
              <span className="incyia-combo-v">
                {focusTarget.controlLabel || '—'}
              </span>
            </div>
            <div className="incyia-combo-item">
              <span className="incyia-combo-k">{t('Subdomain')}</span>
              <span className="incyia-combo-v">
                {focusTarget.subDomainLabel || '—'}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      <div className="maturity-kpis" data-tour-id="maturity-kpis">
        <div className="maturity-kpi">
          <div className="maturity-kpi-label">
            {t('maturityManagementPage.families')}
          </div>
          <div className="maturity-kpi-value">{securityAll?.length || 0}</div>
          <div className="maturity-kpi-desc">
            {t('maturityManagementPage.familiesDesc')}
          </div>
        </div>
        <div className="maturity-kpi">
          <div className="maturity-kpi-label">
            {t('maturityManagementPage.checksInScope')}
          </div>
          <div className="maturity-kpi-value">
            {applicableCount}
            <span> / {securityControls?.length || 0}</span>
          </div>
          <div className="maturity-kpi-desc">
            {t('maturityManagementPage.checksInScopeDesc')}
          </div>
        </div>
        <div className="maturity-kpi">
          <div className="maturity-kpi-label">
            {t('maturityManagementPage.currentFamily')}
          </div>
          <div className="maturity-kpi-value maturity-kpi-value--sm">
            {activeFamily?.LabelSCF
              ? formatTitle(activeFamily.LabelSCF)
              : '—'}
          </div>
          <div className="maturity-kpi-desc">
            {t('maturityManagementPage.currentFamilyDesc')}
          </div>
        </div>
      </div>

      {hasRole(roles, UserRole.IT) || hasRole(roles, UserRole.OTHERS) ? null : (
        <div className="maturity-card" data-tour-id="maturity-families">
          {showFamiliesLoader ? (
            <div className="maturity-empty">
              <Loader />
            </div>
          ) : (
            <>
              <div className="maturity-toolbar">
                <span className="maturity-toolbar-label">
                  {t('maturityManagementPage.familyLabel')}
                </span>
                {securityAll?.map((security, index) => (
                  <button
                    type="button"
                    key={security._id || security.LabelSCF}
                    className={`maturity-chip ${value === index ? 'on' : ''}`}
                    onClick={(event) => handleChange(event, index)}
                  >
                    <span className="maturity-chip-code">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    {formatTitle(security.LabelSCF)}
                  </button>
                ))}
              </div>

              <div className="maturity-toolbar maturity-toolbar--soft">
                <label className="maturity-search">
                  <SearchIcon sx={{ fontSize: 18, color: '#5f7484' }} />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t('filterByName')}
                  />
                </label>
                {canManageApplicability && (
                  <>
                    <button
                      type="button"
                      className="maturity-chip"
                      onClick={() => handleBatchApplicable(true)}
                    >
                      {t('maturityManagementPage.enableAll')}
                    </button>
                    <button
                      type="button"
                      className="maturity-chip"
                      onClick={() => handleBatchApplicable(false)}
                    >
                      {t('maturityManagementPage.disableAll')}
                    </button>
                  </>
                )}
                <span className="maturity-count">
                  {t('maturityManagementPage.checksCount', {
                    total: filteredControls.length,
                    inScope: applicableCount,
                  })}
                </span>
              </div>

              <div className="mlist">
                {loadingSecurityCheckFamily && familiesReady ? (
                  <div className="maturity-empty">
                    <Loader />
                  </div>
                ) : filteredControls.length === 0 ? (
                  <div className="maturity-empty">
                    {t('maturityManagementPage.noChecks')}
                  </div>
                ) : (
                  filteredControls.map(({ control, ctrlIndex }) => {
                    const panelId = `panel${value}-${ctrlIndex}`
                    const currentFamilyLabel = activeFamily?.LabelSCF || ''
                    const isOpen = expanded[panelId] || false
                    const controlCode =
                      control.Source || control.LabelSCF || '—'
                    const level =
                      typeof control.SCScore === 'number'
                        ? t('maturityManagementPage.levelOf', {
                            level: formatDecimal(Number(control.SCScore)),
                          })
                        : '—'

                    return (
                      <React.Fragment key={control._id || panelId}>
                        <button
                          type="button"
                          data-maturity-panel={panelId}
                          className={`mrow ${
                            control.Applicable ? '' : 'off'
                          } ${isOpen ? 'is-open' : ''}`}
                          onClick={(event) =>
                            handleChangeExp(
                              panelId,
                              control._id || '',
                              control.Label || '',
                              currentFamilyLabel
                            )(event, !isOpen)
                          }
                          aria-expanded={isOpen}
                        >
                          <span className="mrow-main">
                            <span className="mcode">{controlCode}</span>
                            <span className="mname">
                              {formatTitle(control.Label)}
                            </span>
                          </span>
                          <span className="mrow-meta">
                            <span
                              className={`maturity-pill ${
                                control.Applicable ? 'in' : 'out'
                              }`}
                            >
                              <span className="dot" />
                              {control.Applicable
                                ? t('maturityManagementPage.inScope')
                                : t('maturityManagementPage.excluded')}
                            </span>
                            <span className="mlevel">{level}</span>
                          </span>
                          {canManageApplicability && (
                            <span
                              className={`maturity-switch ${
                                control.Applicable ? 'on' : ''
                              }`}
                              role="switch"
                              aria-checked={Boolean(control.Applicable)}
                              tabIndex={0}
                              onClick={(event) => {
                                event.stopPropagation()
                                void handleUpdateApplSecurity(control)
                              }}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  void handleUpdateApplSecurity(control)
                                }
                              }}
                            >
                              <span />
                            </span>
                          )}
                        </button>

                        {isOpen && (
                          <div className="mrow-panel">
                            <div className="maturity-grid-wrap">
                              <DataGrid
                                loading={showLoader}
                                disableColumnMenu
                                disableColumnSelector
                                disableRowSelectionOnClick
                                disableVirtualization
                                rows={subDomains || []}
                                columns={getColumnsWithControlContext(
                                  control,
                                  value,
                                  panelId,
                                  currentFamilyLabel
                                )}
                                hideFooterPagination
                                hideFooter
                                getRowId={(row) => row._id}
                                getRowClassName={(params) =>
                                  focusTarget?.subDomainId &&
                                  params.id === focusTarget.subDomainId
                                    ? 'maturity-focus-row'
                                    : ''
                                }
                                getRowHeight={() => 'auto'}
                                columnHeaderHeight={44}
                                autoHeight
                                sx={{
                                  ...listPageDataGridSx(
                                    (subDomains || []).length
                                  ),
                                  width: '100%',
                                  height: 'auto !important',
                                  minHeight:
                                    (subDomains || []).length === 0
                                      ? 160
                                      : undefined,
                                  '& .MuiDataGrid-main': {
                                    width: '100%',
                                  },
                                  '& .MuiDataGrid-columnHeaderTitle': {
                                    letterSpacing: '0.04em !important',
                                    overflow: 'visible !important',
                                    textOverflow: 'clip !important',
                                  },
                                  '& .MuiDataGrid-columnHeader[data-field="Action"] .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeader[data-field="Maturity Scoring"] .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeader[data-field="Weight"] .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeader[data-field="TargetScore"] .MuiDataGrid-columnHeaderTitleContainer':
                                    {
                                      justifyContent: 'center !important',
                                    },
                                  '& .MuiDataGrid-cell[data-field="Action"], & .MuiDataGrid-cell[data-field="Maturity Scoring"], & .MuiDataGrid-cell[data-field="Weight"], & .MuiDataGrid-cell[data-field="TargetScore"]':
                                    {
                                      justifyContent: 'center !important',
                                    },
                                  '& .MuiDataGrid-cell': {
                                    whiteSpace: 'normal !important',
                                    alignItems: 'center',
                                    py: '12px !important',
                                    minHeight: '56px !important',
                                    maxHeight: 'none !important',
                                  },
                                  '& .MuiDataGrid-row': {
                                    minHeight: '56px !important',
                                    maxHeight: 'none !important',
                                  },
                                  '& .MuiDataGrid-row.maturity-focus-row': {
                                    backgroundColor: '#fff8f6',
                                  },
                                }}
                                slots={listPageGridSlots}
                              />
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    )
                  })
                )}
              </div>
            </>
          )}
        </div>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        scroll="body"
        PaperProps={{
          className: 'incyia-modal-paper maturity-weight-modal',
          sx: {
            overflow: 'hidden',
            maxHeight: 'none',
          },
        }}
      >
        <FixWeightTargetScore
          values={SubDomain}
          onChange={handleInputChangeSub}
          onSubmit={handleUpdateSubDomain}
          onCancel={handleClose}
          fields={[
            {
              type: 'number',
              label: t('popups.fixWeightTargetScore.weight'),
              name: 'Weight',
              required: true,
            },
            {
              type: 'number',
              label: t('popups.fixWeightTargetScore.targetScore'),
              name: 'TargetScore',
              required: true,
              fullWidth: true,
            },
          ]}
        />
      </Dialog>
      <MaturityScoringModal
        open={open2}
        onClose={handleClose2}
        title={t('popups.addMaturityScoring.title')}
        maturityData={Maturity}
        onAnswerScoreChange={handleAnswerScoreChange}
        onAdd={handleAddMarurity}
        onFileChange={handleFileChange}
        fileName={fileName}
        proofFile={Maturity.proof}
        subDomainId={Maturity.SubDomainId}
        mode="add"
        t={t}
        TargetScore={targetScore}
      />
    </div>
  )
}
